import type { CSSProperties } from 'react';

import type { ChallengeGrid } from '../api/models';
import { cellAt } from '../api/models';

import { SYMBOL_SHAPES } from './geometry';
import { toPathData } from './symbolPainter';
import type { SymbolCommand, SymbolShape } from './symbolShape';
import type { SymbolToken } from './symbolParser';
import { gridCellAt, gridColumnCount, parseSymbol } from './symbolParser';

const VIEWPORT = 24;

/**
 * Draws one API token. Mirrors android/…/Symbols/SymbolView.kt and ios/…/Symbols/SymbolView.swift.
 *
 * **Transform order is rotate, then mirror**, and it is not negotiable: reversing it produces a different
 * image for `arrow:90:mirrored`, which is the exact token the mirror-or-rotation task uses to tell whether
 * a child confuses the two. Getting it backwards would make that task lie about the child.
 *
 * CSS applies `transform` functions right-to-left, so `rotate(90deg) scaleX(-1)` would mirror **first**.
 * The order written below — `scaleX(-1) rotate(90deg)` — is the one that rotates first. It reads backwards
 * and it is correct; see divergence 16 in the parity register.
 *
 * A token this build does not know draws the placeholder from symbol contract §5 — never blank, never a
 * crash, and never a substituted symbol, which would silently change the question.
 */
export function SymbolView({
  token,
  size = 64,
  color = 'var(--text-primary)',
}: {
  token: string;
  size?: number;
  color?: string;
}) {
  const parsed = parseSymbol(token);

  switch (parsed.kind) {
    case 'named':
      return <NamedSymbol token={parsed} size={size} color={color} />;
    case 'digit':
      return <DigitSymbol value={parsed.value} size={size} color={color} />;
    case 'grid':
      return (
        <GridTokenView
          rows={parsed.rows}
          cellSize={Math.max(16, Math.round(size / Math.max(parsed.rows.length, 1)))}
        />
      );
    default:
      return <UnknownSymbol raw={parsed.raw} size={size} />;
  }
}

function NamedSymbol({
  token,
  size,
  color,
}: {
  token: Extract<SymbolToken, { kind: 'named' }>;
  size: number;
  color: string;
}) {
  const shape: SymbolShape | undefined = SYMBOL_SHAPES[token.name];

  if (shape === undefined) return <UnknownSymbol raw={token.name} size={size} />;

  const strokeWidth = (size / VIEWPORT) * 2;
  const transform = `${token.isMirrored ? 'scaleX(-1) ' : ''}rotate(${token.rotationDegrees}deg)`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEWPORT} ${VIEWPORT}`}
      style={{ transform, display: 'block', overflow: 'visible' }}
      data-symbol={token.name}
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      {shape.fills.map((path: SymbolCommand[], index: number) => (
        <path key={`fill-${index}`} d={toPathData(path)} fill={color} />
      ))}
      {shape.strokes.map((path: SymbolCommand[], index: number) => (
        <path
          key={`stroke-${index}`}
          d={toPathData(path)}
          fill="none"
          stroke={color}
          strokeWidth={(strokeWidth * VIEWPORT) / size}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

/** A digit in a sequence — drawn as a numeral, not as a symbol. */
function DigitSymbol({ value, size, color }: { value: number; size: number; color: string }) {
  return <span style={{ ...cellBox(size), background: 'var(--surface-sunken)', color }}>{value}</span>;
}

/**
 * The placeholder for a token this build does not understand.
 *
 * Challenges are code, so the backend can ship a symbol before a client does. Showing something neutral
 * keeps the task answerable — a child can still compare four unknown shapes if they differ — whereas a
 * blank box or a substituted symbol would change what is being asked.
 */
function UnknownSymbol({ raw, size }: { raw: string; size: number }) {
  reportUnknownToken(raw);

  return (
    <span
      data-symbol="unknown"
      style={{ ...cellBox(size), background: 'var(--surface-sunken)', color: 'var(--text-disabled)' }}
    >
      ?
    </span>
  );
}

const reported = new Set<string>();

/** Mirrors `Platform/Diagnostics.kt`: reported once per token, so a bad stimulus does not flood the log. */
function reportUnknownToken(raw: string): void {
  if (reported.has(raw)) return;

  reported.add(raw);
  // eslint-disable-next-line no-console
  console.warn(`[symbols] Unknown token: ${raw}`);
}

const cellBox = (size: number): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: size,
  height: size,
  borderRadius: 'var(--radius-s)',
  fontSize: '17px',
  fontWeight: 600,
});

/**
 * A board of cells — symbol contract §4.
 *
 * The stimulus and the options **must use the same cell drawing** at different sizes. A child compares
 * them directly, so any difference in how a filled cell looks makes the comparison harder than the task
 * itself. One component serves both, with only the cell size differing.
 */
export function GridTokenView({ rows, cellSize = 32 }: { rows: string[]; cellSize?: number }) {
  return (
    <GridOfCells
      rows={rows.length}
      columns={gridColumnCount(rows)}
      valueAt={(row, column) => gridCellAt(rows, row, column)}
      cellSize={cellSize}
    />
  );
}

/**
 * The same board as it arrives on a stimulus, with the columns and cells the API sent.
 *
 * The **whole** cell value is passed on. It used to be truncated to its first character — right for
 * `spatial.axial-symmetry`, whose cells really are `#` and `.`, and fatal for every grid whose cells are
 * symbol names: `"bee"` became `"b"` and rendered as an empty box. `memory.what-disappeared` asked which
 * object had vanished from a board of six blanks, and `logic.matrix` had been showing an empty matrix
 * without anyone reporting it (R0104).
 */
export function GridView({ grid, cellSize = 44 }: { grid: ChallengeGrid; cellSize?: number }) {
  return (
    <GridOfCells
      rows={grid.rows}
      columns={grid.columns}
      valueAt={(row, column) => cellAt(grid, row, column)}
      cellSize={cellSize}
    />
  );
}

function GridOfCells({
  rows,
  columns,
  valueAt,
  cellSize,
}: {
  rows: number;
  columns: number;
  valueAt: (row: number, column: number) => string;
  cellSize: number;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, ${cellSize}px)`,
        gap: 'var(--space-1)',
        borderRadius: 'var(--radius-m)',
      }}
      role="img"
      aria-hidden="true"
    >
      {Array.from({ length: rows * columns }, (_unused, index: number) => (
        <GridCell key={index} value={valueAt(Math.floor(index / columns), index % columns)} size={cellSize} />
      ))}
    </div>
  );
}

function GridCell({ value, size }: { value: string; size: number }) {
  const base: CSSProperties = { width: size, height: size, borderRadius: 'var(--radius-s)' };

  if (value === '#') return <span style={{ ...base, background: 'var(--action-primary)' }} />;

  // The mirror axis in `spatial.axial-symmetry` — a thin rule, not a cell (R0112).
  if (value === '|') {
    return (
      <span style={{ ...base, display: 'grid', placeItems: 'center' }}>
        <span style={{ width: 2, height: size, background: 'var(--border-strong)', borderRadius: 1 }} />
      </span>
    );
  }

  if (value === '?') {
    return (
      <span
        style={{
          ...base,
          background: 'var(--surface-canvas)',
          border: '2px dashed var(--border-strong)',
        }}
      />
    );
  }

  if (value.length === 1 && value >= '0' && value <= '9') {
    return <span style={{ ...cellBox(size), background: 'var(--surface-sunken)' }}>{value}</span>;
  }

  // Anything longer than a marker is a symbol name — `bee`, `trianglex1` — and renders as that symbol.
  // An unknown name draws nothing rather than a substitute, which would silently change the question.
  if (value !== '.' && value.length > 1) {
    return (
      <span style={{ ...base, display: 'grid', placeItems: 'center' }}>
        <SymbolView token={value} size={size - 6} />
      </span>
    );
  }

  return (
    <span
      style={{ ...base, background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}
    />
  );
}
